import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { BarChart } from '@mui/x-charts/BarChart';
import { useQuery } from '@tanstack/react-query';
import {
  fetchLoginActivity,
  fetchUserLoginHistory,
  type UserLastLogin,
} from '../api/loginActivity';
import type { SessionChain } from '../api/sessions';
import { usePermission } from '../hooks/usePermission';

type SessionGroup = {
  key: string;
  username: string;
  day: string;
  first_seen: number;
  last_seen: number;
  ip: string;
  user_agent: string;
  count: number;
};

function groupActiveSessions(sessions: SessionChain[]): SessionGroup[] {
  const map = new Map<string, SessionGroup>();
  for (const s of sessions) {
    const day = new Date(s.started_at * 1000).toLocaleDateString('en-CA');
    const key = `${day}|${s.ip}|${s.user_agent}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { key, username: s.username, day, first_seen: s.started_at, last_seen: s.last_seen, ip: s.ip, user_agent: s.user_agent, count: 1 });
    } else {
      existing.count++;
      existing.first_seen = Math.min(existing.first_seen, s.started_at);
      existing.last_seen = Math.max(existing.last_seen, s.last_seen);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.first_seen - a.first_seen);
}

function formatHourLabel(hour: string): string {
  // "2026-05-02T14" → "05-02 14h"
  const [datePart, timePart] = hour.split('T');
  const [, month, day] = datePart.split('-');
  return `${month}-${day} ${timePart}h`;
}

function GeoChip({ country, city }: { country: string; city?: string }) {
  return (
    <Chip
      label={city ? `${country} / ${city}` : country}
      size="small"
      variant="outlined"
      sx={{ fontSize: '0.7rem', height: 20 }}
    />
  );
}

function UserHistoryDialog({ user, onClose }: { user: UserLastLogin | null; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['login-history', user?.username],
    queryFn: () => fetchUserLoginHistory(user!.username),
    enabled: !!user,
  });

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Login history — {user?.username}
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={24} />}
        {data && data.history.length === 0 && (
          <Typography color="text.secondary">No login records found.</Typography>
        )}
        {data && data.history.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.history.map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {new Date(r.ts).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.result}
                      size="small"
                      color={r.result === 'success' ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.ip}</TableCell>
                  <TableCell>
                    {r.geo ? <GeoChip country={r.geo.country} city={r.geo.city} /> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function LoginActivity() {
  const canRead = usePermission('security.read');
  const [selectedUser, setSelectedUser] = useState<UserLastLogin | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['login-activity'],
    queryFn: fetchLoginActivity,
    enabled: canRead,
    refetchInterval: 60_000,
  });

  if (!canRead) {
    return <Typography color="error">You do not have permission to view login activity.</Typography>;
  }

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography color="error">Failed to load login activity.</Typography>;
  if (!data) return null;

  // Downsample: show one tick per 6 hours to avoid crowding x-axis
  const chartData = data.logins_per_hour;
  const tickEvery = 6;
  const xLabels = chartData.map((b, i) => i % tickEvery === 0 ? formatHourLabel(b.hour) : '');
  const successSeries = chartData.map((b) => b.successful);
  const failedSeries = chartData.map((b) => b.failed);

  const totalSuccessful = successSeries.reduce((s, v) => s + v, 0);
  const totalFailed = failedSeries.reduce((s, v) => s + v, 0);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Login Activity</Typography>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Chip label={`${totalSuccessful} successful (7d)`} color="success" size="small" />
        <Chip label={`${totalFailed} failed (7d)`} color="error" size="small" />
        <Chip label={`${data.active_sessions.length} active sessions`} color="info" size="small" />
      </Box>

      {/* Logins per hour chart */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Logins per hour — last 7 days" titleTypographyProps={{ variant: 'h6' }} />
        <CardContent sx={{ pt: 0 }}>
          <BarChart
            xAxis={[{ scaleType: 'band', data: xLabels }]}
            series={[
              { data: successSeries, label: 'Successful', color: '#4caf50', stack: 'logins' },
              { data: failedSeries, label: 'Failed', color: '#e53935', stack: 'logins' },
            ]}
            height={220}
            margin={{ left: 40, right: 16, top: 16, bottom: 40 }}
          />
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Top failing IPs */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Top failing IPs" titleTypographyProps={{ variant: 'h6' }} />
            <CardContent sx={{ pt: 0 }}>
              {data.top_failing_ips.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No failed logins recorded.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>IP</TableCell>
                      <TableCell align="right">Attempts</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.top_failing_ips.map((row) => (
                      <TableRow key={row.ip}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.ip}</TableCell>
                        <TableCell align="right">
                          <Chip label={row.count} size="small" color="error" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {row.geo ? <GeoChip country={row.geo.country} city={row.geo.city} /> : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Per-user last login */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Last login per user" titleTypographyProps={{ variant: 'h6' }} />
            <CardContent sx={{ pt: 0 }}>
              {data.last_login_per_user.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No login records in last 7 days.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Last login</TableCell>
                      <TableCell>IP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.last_login_per_user.map((row) => (
                      <TableRow key={row.username} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedUser(row)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.username}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={row.last_login}>
                            <Typography variant="body2">{new Date(row.last_login).toLocaleString()}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {row.ip}
                          {row.geo && (
                            <Box mt={0.25}>
                              <GeoChip country={row.geo.country} city={row.geo.city} />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active sessions summary */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Active sessions"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <Button size="small" href="/sessions">
                  Manage sessions
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              {data.active_sessions.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No active sessions.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Day first seen</TableCell>
                      <TableCell>Last seen</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>User-Agent</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupActiveSessions(data.active_sessions).map((g) => (
                      <TableRow key={g.key}>
                        <TableCell>{g.username}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                          {g.day}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(g.last_seen * 1000).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{g.ip}</TableCell>
                        <TableCell>
                          <Tooltip title={g.user_agent}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {g.user_agent}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={g.count} size="small" variant="outlined" color="info" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <UserHistoryDialog user={selectedUser} onClose={() => setSelectedUser(null)} />

      <Divider sx={{ mt: 3 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Data sourced from audit log · last 7 days · refreshes every 60 s
      </Typography>
    </Box>
  );
}
